import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  Alteration,
  AlterationCollectInput,
  AlterationMessages,
  AlterationPage,
  AlterationPaymentResult,
  AlterationPayments,
  AlterationStatus,
  MessageLogEntry,
  NotificationEvent,
  OrderMessagePreview,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  createAlteration,
  getAlteration,
  getAlterationMessages,
  getAlterationPayments,
  getAlterations,
  moveAlteration,
  previewAlterationMessage,
  reverseAlterationPayment,
  sendAlterationMessage,
  type AlterationListParams,
} from './alterations-api.js';

export const alterationKeys = {
  all: ['alterations'] as const,
  list: (params: AlterationListParams) => ['alterations', 'list', params] as const,
  one: (alterationId: string) => ['alterations', 'one', alterationId] as const,
  payments: (alterationId: string) => ['alterations', 'payments', alterationId] as const,
  messages: (alterationId: string) => ['alterations', 'messages', alterationId] as const,
  preview: (alterationId: string, event: NotificationEvent) =>
    ['alterations', 'messages', alterationId, 'preview', event] as const,
};

export function useAlteration(alterationId: string | null): UseQueryResult<Alteration> {
  const client = useApiClient();
  return useQuery({
    queryKey: alterationKeys.one(alterationId ?? ''),
    queryFn: () => getAlteration(client, alterationId ?? ''),
    enabled: Boolean(alterationId),
  });
}

export function useAlterations(params: AlterationListParams = {}): UseQueryResult<AlterationPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: alterationKeys.list(params),
    queryFn: () => getAlterations(client, params),
    // Keeps the list on screen while a filter change loads, so rows do not blink.
    placeholderData: (previous) => previous,
  });
}

export function useCreateAlteration(): UseMutationResult<
  Alteration,
  Error,
  Record<string, unknown>
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => createAlteration(client, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: alterationKeys.all });
    },
  });
}

export function useMoveAlteration(): UseMutationResult<
  Alteration,
  Error,
  {
    alterationId: string;
    toStatus: AlterationStatus;
    reason?: string | null;
    collect?: AlterationCollectInput | null;
  }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ alterationId, toStatus, reason, collect }) =>
      moveAlteration(client, alterationId, {
        toStatus,
        ...(reason ? { reason } : {}),
        ...(collect ? { collect } : {}),
      }),
    onSuccess: (_moved, { collect }) => {
      void queryClient.invalidateQueries({ queryKey: alterationKeys.all });
      // Money taken at the hand-over lands in the day book like any other payment.
      if (collect) void queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

/** The alteration's own ledger. Not asked for by a tailor, whom the API refuses. */
export function useAlterationPayments(
  alterationId: string | null,
): UseQueryResult<AlterationPayments> {
  const client = useApiClient();
  return useQuery({
    queryKey: alterationKeys.payments(alterationId ?? ''),
    queryFn: () => getAlterationPayments(client, alterationId ?? ''),
    enabled: Boolean(alterationId),
  });
}

/** An owner's correction — direct, never queued, for the reason `useReversePayment` gives. */
export function useReverseAlterationPayment(
  alterationId: string,
): UseMutationResult<AlterationPaymentResult, unknown, { paymentId: string; reason: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, reason }) =>
      reverseAlterationPayment(client, alterationId, paymentId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: alterationKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useAlterationMessages(
  alterationId: string | null,
): UseQueryResult<AlterationMessages> {
  const client = useApiClient();
  return useQuery({
    queryKey: alterationKeys.messages(alterationId ?? ''),
    queryFn: () => getAlterationMessages(client, alterationId ?? ''),
    enabled: Boolean(alterationId),
  });
}

/** Fetched only while the dialog is open and never held, like the order card's preview. */
export function useAlterationMessagePreview(
  alterationId: string,
  event: NotificationEvent | null,
): UseQueryResult<OrderMessagePreview> {
  const client = useApiClient();
  return useQuery({
    queryKey: alterationKeys.preview(alterationId, event ?? 'alteration_ready'),
    queryFn: () => previewAlterationMessage(client, alterationId, event as NotificationEvent),
    enabled: Boolean(event),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useSendAlterationMessage(
  alterationId: string,
): UseMutationResult<MessageLogEntry, unknown, NotificationEvent> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (event: NotificationEvent) => sendAlterationMessage(client, alterationId, event),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: alterationKeys.messages(alterationId) });
    },
  });
}
